import React, {
  Context,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useNotificationsContext } from './Notifications';
import { useDialogsContext } from './Dialog';
import { useEventTrackerContext } from './EventTracker';
import {
  pathname,
  stage as stageEnum,
  sortByDate,
  formatDateForDb,
  lineStatus,
  createNewLine,
  validateLineLength,
  validateLineNameUnique,
  setRepayStartDateNewLine,
  setCompleteDateNewLine,
  createTimelineInfo,
  applicationStatus,
  isLinesOnboardingPage,
  updateTimelineToShowLine,
  ApplicationType,
  getLinesForProduct,
  getLastLine,
  formatDate,
  logger,
} from '../utils';
import {
  deleteLine,
  getLines,
  cloneApplication,
  editLineV2,
  requestChangeActiveLine,
  editLineNameV2,
  appCreatedByAdmin,
} from '../components/API';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbarContext } from './Snackbar';
import { useAuthContext } from './Auth';
import { ILine } from '../global';
import { ITimelineInfo, IProduct, ENotificationTypes } from '../Types';
import EditActiveLineDialog, {
  steps as editActiveLineSteps,
} from '../components/lines/requestChange/Dialog';
import moment, { Moment } from 'moment';
import cloneDeep from 'lodash.clonedeep';
import allStages from '../components/stages';
import IApplicationBasic from '../Types/application';
import DuplicateLineDialog from '../components/lines/duplicate/Dialog';
import AutoCreateLineDialog from '../components/lines/autoCreate/Dialog';
import DeleteDialog from '../components/lines/DeleteLineDialog';
import DeleteManyDialog from '../components/modals/BulkDeleteDialog';
import UploadDocumentDialog from '../components/uploadDocument/Dialog';
import AddProductDialog from '../components/Product/AddProductDialog';
import messages from '../messages';

function updateStageStartDateToUTC(stage) {
  return {
    ...stage,
    start: formatDateForDb(moment.utc(stage.start)),
  };
}

export interface ILinesContext {
  loading: boolean;
  setLoading: (...args) => any;
  lines: Array<ILine>;
  setLines: (...args) => any;
  isLineSelected: boolean;
  setIsLineSelected: (...args) => any;
  selectedLine: ILine;
  setSelectedLine: (...args) => any;
  selectedLines: Array<any>;
  setSelectedLines: (...args) => any;
  selectedCloneLine: ILine;
  validateLineAfterOnboarding: any;
  setValidateLineAfterOnboarding: (...args) => any;
  timelineInfo: ITimelineInfo;
  setTimelineInfo: (...args) => any;
  firstLineGeneration: boolean;
  setFirstLineGeneration: (...args) => any;
  lineOnEditMode: boolean;
  setLineOnEditMode: (...args) => any;
  products: Array<IProduct>;
  setProducts: (...args) => any;
  applications: Array<IApplicationBasic>;
  setApplications: (...args) => any;
  lineOnShow: string;
  setLineOnShow: (...args) => any;
  isCreatingFirst3Lines: () => any;
  createLineFromScratch: (...args) => any;
  createLineClick: (...args) => any;
  deleteLineClick: (...args) => any;
  editProductClick: (...args) => any;
  deleteManyClick: (...args) => any;
  uploadToLineClick: (...args) => any;
  changeActiveLineClick: (...args) => any;
  duplicateLineSubmit: (...args) => any;
  editActiveLineClick: (...args) => any;
  editActiveLineStep: (...args) => any;
  editActiveLineSubmit: (...args) => any;
  editActiveLineSave: (...args) => any;
  editLineClick: (line: ILine) => any;
  editActiveLineNameClick: (...args) => any;
  autoCreateLinesClick: (...args) => any;
  editLineSubmit: (line: ILine) => any;
  editLineCancel: (lineId: string) => any;
  showLineInTimeline: (...args) => void;
}

export const LinesContext: Context<ILinesContext> = createContext({
  loading: null,
  setLoading: () => {},
  lines: [],
  setLines: () => {},
  isLineSelected: null,
  setIsLineSelected: () => {},
  selectedLine: null,
  setSelectedLine: () => {},
  selectedLines: null,
  setSelectedLines: () => {},
  selectedCloneLine: null,
  validateLineAfterOnboarding: false,
  setValidateLineAfterOnboarding: () => {},
  timelineInfo: null,
  setTimelineInfo: () => {},
  firstLineGeneration: null,
  setFirstLineGeneration: () => {},
  lineOnEditMode: null,
  setLineOnEditMode: () => {},
  products: null,
  setProducts: () => {},
  applications: null,
  setApplications: () => {},
  lineOnShow: null,
  setLineOnShow: () => {},
  isCreatingFirst3Lines: () => {},
  createLineFromScratch: () => {},
  createLineClick: () => {},
  deleteLineClick: () => {},
  editProductClick: () => {},
  deleteManyClick: () => {},
  uploadToLineClick: () => {},
  changeActiveLineClick: () => {},
  duplicateLineSubmit: () => {},
  editActiveLineClick: () => {},
  editActiveLineStep: () => {},
  editActiveLineSubmit: () => {},
  editActiveLineSave: () => {},
  editLineClick: () => {},
  editActiveLineNameClick: () => {},
  autoCreateLinesClick: () => {},
  editLineSubmit: () => {},
  editLineCancel: () => {},
  showLineInTimeline: () => {},
});

export default function LinesContextProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState(null);
  const [isLineSelected, setIsLineSelected] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedLines, setSelectedLines] = useState(null);
  const [selectedCloneLine, setSelectedCloneLine] = useState(null);
  const [validateLineAfterOnboarding, setValidateLineAfterOnboarding] =
    useState(false);
  const [products, setProducts] = useState(null);
  const [applications, setApplications] = useState();
  const [timelineInfo, setTimelineInfo] = useState({
    start: moment(),
    end: moment(),
    totalMonths: 0,
    totalDays: 0,
  });

  const [firstLineGeneration, setFirstLineGeneration] = useState(false);
  const [lineOnEditMode, setLineOnEditMode] = useState(false);
  const [lineOnShow, setLineOnShow] = useState(null);

  const [requestChangeCurrentStep, setRequestChangeCurrentStep] = useState(
    editActiveLineSteps.chooseOption,
  );

  const dialogs: any = useDialogsContext();
  const notifications = useNotificationsContext();
  const snackbar = useSnackbarContext();
  const { signedIn } = useAuthContext();
  const { trackEvent } = useEventTrackerContext();

  function displayDateForStageOnEdit(idx: number) {
    return formatDateForDb(
      moment(timelineInfo.start)
        .add(1, 'month')
        .add(idx * 1, 'months'),
    );
  }

  function showLineInTimeline(line: ILine) {
    updateTimelineToShowLine(line, timelineInfo, setTimelineInfo);
  }

  useEffect(() => {
    if (timelineInfo?.totalDays === 0) {
      const startingTimelineInfo = createTimelineInfo(12);
      setTimelineInfo(startingTimelineInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isCreatingFirst3Lines() {
    const products = [...new Set(lines?.map((item) => item.product))];
    const hasOneProduct = products?.length === 1 || products?.length === 0;
    const hasLessThan3Lines = lines?.length < 3;
    return hasOneProduct && hasLessThan3Lines;
  }

  function createLineFromScratch(productId: string) {
    let updatedLines = sortByDate(cloneDeep(lines));
    updatedLines = updatedLines
      .filter((l) => !l.new)
      .map((l) => {
        l.edit = false;
        return l;
      });
    const lineId = uuidv4();
    let lineStart =
      lines.length === 0
        ? moment()
        : moment(updatedLines[updatedLines.length - 1].start).add(1, 'day');
    const newLine: ILine = {
      new: true,
      edit: true,
      id: lineId,
      name: 'New Line',
      status: lineStatus.draft,
      product: productId,
      inventory: 1000,
      start: formatDateForDb(lineStart),
      stages: allStages
        .filter((stage, i) => i < 6)
        .map((stage, i) => {
          return {
            line: lineId,
            id: uuidv4(),
            name: allStages[i].value,
            amount: null,
            start: displayDateForStageOnEdit(i),
            deleted: false,
            needsDateChange: true,
          };
        }),
    };
    updatedLines.push(newLine);
    setLineOnShow(newLine.id);
    setLines(updatedLines);
    setSelectedLine(newLine);
    setLineOnEditMode(true);
    return lineId;
  }

  function deleteLineClick(line: ILine) {
    setSelectedLine(line);
    dialogs.open('deleteLine');
  }

  function editProductClick(product: IProduct) {
    setSelectedProduct(product);
    dialogs.open('addProduct');
  }

  async function deleteLineSubmit(line: ILine) {
    await deleteLine(line.id);
    setLines(lines.filter((l) => l.id !== line.id));
    notifications.removeNotification(ENotificationTypes.line, line.id);
  }

  function deleteManyClick(lineIds: Array<string>) {
    setSelectedLines(lineIds);
    dialogs.open('bulkDelete');
  }

  function uploadToLineClick(line: ILine) {
    setSelectedLine(line);
    dialogs.open('upload');
  }

  async function openEditActiveLineDialog(line: ILine) {
    setSelectedLine(line);
    let appCreatedByAdminRes;
    try {
      let cloneLine = lines?.find((l) => l.origin_line === line.id);
      if (!cloneLine) {
        setLoading(true);
        let updatedLines = cloneDeep(lines);
        const clonedApp = await cloneApplication({
          id: line.financing_application,
        });
        const cloneAppLines = await getLines(null, clonedApp?.id);
        cloneLine = cloneAppLines.find((l) => l.origin_line === line.id);
        if (!cloneLine) {
          setLoading(false);
          return snackbar.show(messages.lines.changeRequestLineDeleted);
        }
        setLines([...updatedLines, ...cloneAppLines]);
      } else if (cloneLine?.financing_application) {
        appCreatedByAdminRes = await appCreatedByAdmin({
          applicationId: cloneLine.financing_application,
        });
      }

      if (appCreatedByAdminRes?.editByAdmin) {
        snackbar.show(appCreatedByAdminRes.message, 'error');
      } else if (cloneLine?.status === lineStatus.pending) {
        snackbar.show(messages.lines.pendingChangeApproval);
      } else {
        dialogs.open('lineChange');
      }
    } catch (error) {
      const errorMessage =
        error.error === 'HANDLE_BY_ADMIN'
          ? error.message
          : messages.lines.changeRequestSubmitError;
      snackbar.show(errorMessage, 'error');
    }
    setLoading(false);
  }

  async function createLineClick(productId: string) {
    const productLines = getLinesForProduct(lines, productId);
    if (productLines.length === 0) {
      createLineFromScratch(productId);
    } else {
      const lastLine = getLastLine(productLines);
      openDuplicateLineDialog(lastLine);
    }
  }

  async function openDuplicateLineDialog(line: ILine) {
    setSelectedLine(cloneDeep(line));
    dialogs.open('duplicateLine');
  }

  function createDuplicatedLineName(line: ILine) {
    let passedNameCheck = false;
    let lineNumber = 0;
    let newLineName = line.name + ' - copy';
    while (!passedNameCheck || lineNumber > 100) {
      try {
        if (lineNumber === 1) {
          newLineName += ` - ${lineNumber}`;
        }
        if (lineNumber > 1 && lineNumber < 10) {
          newLineName = newLineName.slice(0, -1);
          newLineName += lineNumber;
        }
        if (lineNumber >= 10) {
          newLineName = newLineName.slice(0, -2);
          newLineName += lineNumber;
        }
        validateLineNameUnique(lines, {
          ...line,
          name: newLineName,
        });
        passedNameCheck = true;
      } catch (error) {
        lineNumber += 1;
        if (lineNumber >= 100) {
          throw error;
        }
      }
    }
    return newLineName;
  }

  function duplicateLineSubmit(startDate: Moment) {
    const moveForwardXDays = startDate.diff(
      selectedLine.stages[0].start,
      'days',
    );
    const duplicatedLine = cloneDeep(selectedLine);
    duplicatedLine['name'] = createDuplicatedLineName(selectedLine);
    duplicatedLine.stages = duplicatedLine.stages
      .filter(
        (stage) =>
          stage.name !== stageEnum.repaymentsStart.value &&
          stage.name !== stageEnum.complete.value,
      )
      .map((stage, i) => {
        stage.amount = null;
        const shiftedDate = moment
          .utc(stage.start)
          .add(moveForwardXDays, 'days');
        stage.start = formatDate(shiftedDate);
        return stage;
      });
    const lineId = uuidv4();
    const newLine = {
      ...duplicatedLine,
      new: true,
      edit: true,
      id: lineId,
      name: 'New Line',
      start: duplicatedLine.stages[0].start,
      financing_application: null,
      status: lineStatus.draft,
      origin_line: null,
    };
    setLineOnShow(lineId);
    setLines([...lines, newLine]);
    setSelectedLine(newLine);
    setLineOnEditMode(true);
  }

  function editLineClick(line: ILine) {
    const onboarding = isLinesOnboardingPage(window.location.pathname);
    if (onboarding || line.edit || line.status !== lineStatus.draft) return;
    setSelectedLine(line);
    let updatedLines = cloneDeep(lines);
    updatedLines = updatedLines
      .filter((l) => !l.new)
      .map((l) => {
        if (l.edit && selectedLine) {
          l = { ...selectedLine };
        }
        if (l.id === line.id) {
          l.edit = true;
          setLineOnShow(l.id);
        } else {
          l.edit = false;
        }
        return l;
      });
    setLines(updatedLines);
    setLineOnEditMode(true);
  }

  function editActiveLineNameClick(line: ILine) {
    setSelectedLine(line);
    let updatedLines = cloneDeep(lines);
    updatedLines = updatedLines
      .filter((l) => !l.new)
      .map((l) => {
        if (l.id === line.id) {
          l.editActiveNameLine = true;
          setLineOnShow(l.id);
        } else {
          l.editActiveNameLine = false;
        }
        return l;
      });

    setLines(updatedLines);
  }

  async function autoCreateLinesClick(
    productId: string,
    firstLineGeneration = false,
  ) {
    let linesForProduct = getLinesForProduct(lines, productId);
    if (linesForProduct.length > 0) {
      const lastLine = getLastLine(linesForProduct);
      setSelectedLine(lastLine);
      setFirstLineGeneration(firstLineGeneration);
      dialogs.open('autoCreateLines');
    } else {
      snackbar.show(messages.lines.autoCreate.generateWithoutLinesError);
    }
  }

  async function editActiveLineClick(
    editLeftSide = false,
    editRightSide = false,
    editActiveLine = false,
  ) {
    try {
      let updatedLines = cloneDeep(lines);
      let cloneLine = lines.find(
        (line) => line.origin_line === selectedLine.id,
      );
      if (!cloneLine) {
        throw Error('Clone line does not exist.');
      }
      if (!selectedCloneLine) {
        // SET CLONE LINE FIRST TIME.
        // NEEDED IF USER CANCELS AND WANT TO REVERT FUTURE CHANGES.
        setSelectedCloneLine(cloneDeep(cloneLine));
      }
      updatedLines = updatedLines
        .filter((l) => !l.new)
        .map((l) => {
          if (l.origin_line === selectedLine.id) {
            l.editLeftSide = editLeftSide;
            l.editRightSide = editRightSide;
            l.editActiveLine = editActiveLine;
            l.edit = false;
            if (editActiveLine) {
              setLineOnShow(l.id);
            }
          } else {
            l.edit = false;
            l.editLeftSide = false;
            l.editRightSide = false;
            l.editActiveLine = false;
            l.editActiveNameLine = false;
          }
          return l;
        });
      setLines(updatedLines);
      setLineOnEditMode(true);
    } catch (error) {
      snackbar.show(messages.lines.changeRequestEditError, 'error');
    }
    setLoading(false);
  }

  function editActiveLineSave(lineBeingEdited: ILine) {
    try {
      validateLineNameUnique(lines, lineBeingEdited);
    } catch (error) {
      snackbar.show(messages.lines.lineEditErrors.nameNotUnique, 'error');
      return;
    }
    let linesCopy = cloneDeep(lines);
    linesCopy = linesCopy.map((line) => {
      if (line.id === lineBeingEdited.id) {
        line = {
          ...lineBeingEdited,
          editLeftSide: false,
          editRightSide: false,
        };
      }
      return line;
    });
    setLines(linesCopy);
    dialogs.open('lineChange');
    editActiveLineStep(editActiveLineSteps.chooseOption);
  }

  function editActiveLineStep(currentStep: any) {
    setRequestChangeCurrentStep(currentStep);
  }

  async function editActiveLineSubmit(
    message: string,
    changes: any,
    filesToUpload?: any,
  ) {
    setLoading(true);
    try {
      let line = lines.find((line) => line.origin_line === selectedLine.id);
      if (!line) {
        throw Error('NO_LINE_FOUND.');
      }
      if (changes.cancelLine) {
        await deleteLineSubmit(line);
      } else {
        await editLineSubmit(line);
      }
      const res = await requestChangeActiveLine({
        applicationId: selectedLine?.financing_application,
        newApplicationId: line?.financing_application,
        comment: message,
        changedLineIds: [line.id],
        filesId: filesToUpload,
      });

      snackbar.show(messages.lines.changeActiveLineRequestSuccess, 'success');
      trackEvent({
        event: 'line_change_requested',
        application_type: ApplicationType.CHANGE_REQUEST,
        line,
      });
      if (!res?.existingRequest) {
        trackEvent({
          event: 'application_submitted',
          application_id: line?.financing_application,
          application_type: ApplicationType.CHANGE_REQUEST,
          num_lines: lines?.length,
        });
      }
    } catch (error) {
      let errorMessage = messages.lines.changeRequestSubmitError;
      if (error?.message === 'INVALID_LINE_LENGTH') {
        errorMessage = messages.lines.invalidLineLength;
      }
      if (error?.message === 'LINE_NAME_NOT_UNIQUE') {
        errorMessage = messages.lines.lineEditErrors.nameNotUnique;
      }
      snackbar.show(errorMessage, 'error');
      setLoading(false);
      throw Error(errorMessage);
    }
    setLoading(false);
  }

  async function createLine(line: ILine) {
    const linesCopy = lines ? cloneDeep(lines) : [];
    line['new'] = false;
    const lineEnd = line.stages[line.stages.length - 1]?.start;
    const sellingStart = line.stages.find(
      (stage) => stage.name === stageEnum.sellingStart.value,
    )?.start;
    const repaymentStart = {
      line: line.id,
      id: uuidv4(),
      name: stageEnum.repaymentsStart.value,
      amount: 0,
      start: formatDateForDb(setRepayStartDateNewLine(moment(sellingStart))),
      deleted: false,
    };
    const complete = {
      line: line.id,
      id: uuidv4(),
      name: stageEnum.complete.value,
      amount: 0,
      start: formatDateForDb(
        setCompleteDateNewLine(moment(sellingStart), moment(lineEnd)),
      ),
      deleted: false,
    };
    line.stages.push(repaymentStart);
    line.stages.push(complete);
    line.stages = line.stages.map((stage) => {
      stage['needsDateChange'] = false;
      return stage;
    });
    const newLineInfo = await createNewLine(line);
    newLineInfo.stages = sortByDate(newLineInfo.stages).map(
      updateStageStartDateToUTC,
    );
    const newLine = {
      id: newLineInfo.id,
      name: line.name,
      status: lineStatus.draft,
      inventory: line.inventory,
      product: line.product,
      stages: newLineInfo.stages,
      start: newLineInfo.stages[0].start,
      end: newLineInfo.stages[newLineInfo.stages.length - 1]?.start,
      edit: false,
      new: false,
    };
    const updatedLines = linesCopy.map((l) => {
      if (l.id === line.id) {
        l = { ...newLine };
      }
      return l;
    });
    setLines(updatedLines);
    return newLineInfo.id;
  }

  async function editExistingLine(updatedLine: ILine) {
    updatedLine.stages = updatedLine.stages.filter(
      (stage) =>
        stage.name !== stageEnum.repaymentsStart.value &&
        stage.name !== stageEnum.complete.value,
    );
    updatedLine.stages = sortByDate(updatedLine.stages);
    if (updatedLine.status === applicationStatus.active.status) {
      const clonedLine = lines.find(
        (line) => line.origin_line === updatedLine.id,
      );
      await editLineV2({
        id: clonedLine.id,
        name: updatedLine.name,
        product: clonedLine.product,
        inventory: clonedLine.inventory,
        stages: clonedLine.stages,
      });
    }

    const lineFromServer = await editLineV2({
      id: updatedLine.id,
      name: updatedLine.name,
      product: updatedLine.product,
      inventory: updatedLine.inventory,
      stages: updatedLine.stages,
    });
    updatedLine.stages = sortByDate(lineFromServer.stages);
    let lineStart = updatedLine.stages[0]?.start;
    let lineEnd = updatedLine.stages[updatedLine.stages?.length - 1]?.start;
    setLines(
      lines.map((line) => {
        if (line.id === updatedLine.origin_line) {
          return {
            ...line,
            isChangeRequested: true,
          };
        }
        if (line.id === updatedLine.id) {
          return {
            ...line,
            name: updatedLine.name,
            product: updatedLine.product,
            inventory: updatedLine.inventory,
            start: lineStart,
            end: lineEnd,
            createdByAi: false,
            stages: updatedLine.stages.map(updateStageStartDateToUTC),
            edit: false,
            editLeftSide: false,
            editRightSides: false,
            editActiveLine: false,
            editActiveNameLine: false,
          };
        }
        return line;
      }),
    );
  }

  async function editActiveLineName(updatedLine: ILine) {
    await editLineNameV2({ id: updatedLine.id, name: updatedLine.name });
    const clonedLine = lines.find(
      (clonedLine) => clonedLine.origin_line === updatedLine.id,
    );
    if (clonedLine)
      await editLineNameV2({ id: clonedLine.id, name: updatedLine.name });

    setLines(
      lines.map((line) => {
        if (line.id === clonedLine.id) {
          return {
            ...line,
            name: updatedLine.name,
          };
        }
        if (line.id === updatedLine.id) {
          return {
            ...line,
            name: updatedLine.name,
            editActiveNameLine: false,
          };
        }
        return line;
      }),
    );
  }

  async function editLineSubmit(line: ILine) {
    try {
      logger.info({ message: 'Start edit or create line', data: { line } });
      try {
        validateLineLength(line.stages);
      } catch (e) {
        throw Error('INVALID_LINE_LENGTH');
      }
      validateLineNameUnique(lines, line);
      let lineId = line.id;
      if (['complete', 'active'].includes(line.status)) {
        await editActiveLineName(line);
      } else {
        if (!line.new) {
          await editExistingLine(line);
        } else {
          const newLineId = await createLine(line);
          lineId = newLineId;
        }
      }
      setLineOnEditMode(false);
      logger.info({
        message: 'Finished edit / create line submit',
        data: {
          line: { ...line, lineId },
        },
      });
      return lineId;
    } catch (error) {
      logger.error({ message: 'edit_line_error', data: { line }, error });
      throw error;
    }
  }

  function editLineCancel(lineId: string) {
    const linesCopy = cloneDeep(lines);
    const state = {
      edit: false,
      editActiveLine: false,
      editLeftSide: false,
      editRightSide: false,
    };
    const updatedLines = linesCopy
      .filter((line) => !line.new)
      .map((line) => {
        if (line.id === lineId) {
          line = line.editActiveLine ? selectedCloneLine : selectedLine;
          line = { ...line, ...state };
        }
        return line;
      });
    setLines(updatedLines);
    setSelectedLine(null);
    setSelectedCloneLine(null);
    setLineOnEditMode(false);
  }

  useEffect(() => {
    if (!signedIn) {
      lines && setLines(null);
      products && setProducts(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  return (
    <LinesContext.Provider
      value={{
        loading,
        setLoading,
        lines,
        setLines,
        isLineSelected,
        selectedLine,
        setSelectedLine,
        setIsLineSelected,
        selectedLines,
        setSelectedLines,
        selectedCloneLine,
        validateLineAfterOnboarding,
        setValidateLineAfterOnboarding,
        timelineInfo,
        setTimelineInfo,
        firstLineGeneration,
        setFirstLineGeneration,
        lineOnEditMode,
        setLineOnEditMode,
        products,
        setProducts,
        applications,
        setApplications,
        lineOnShow,
        setLineOnShow,
        isCreatingFirst3Lines,
        createLineFromScratch,
        createLineClick,
        deleteLineClick,
        editProductClick,
        deleteManyClick,
        uploadToLineClick,
        changeActiveLineClick: openEditActiveLineDialog,
        duplicateLineSubmit,
        editActiveLineClick,
        editActiveLineSubmit,
        editActiveLineStep,
        editActiveLineSave,
        editLineClick,
        editActiveLineNameClick,
        autoCreateLinesClick,
        editLineSubmit,
        editLineCancel,
        showLineInTimeline,
      }}
    >
      {children}
      {window.location.pathname === pathname.lines.url && (
        <>
          <DuplicateLineDialog startDate={selectedLine?.start} />
          <DeleteDialog
            open={dialogs.deleteLine}
            handleClose={() => dialogs.close('deleteLine')}
            lineName={selectedLine?.name}
            deleteLineClick={async () => {
              dialogs.close('deleteLine');
              setLoading(true);
              try {
                await deleteLineSubmit(selectedLine);
                snackbar.show(
                  messages.lines.success(selectedLine.name, 'deleted'),
                  'delete',
                );
              } catch (error) {
                snackbar.show(messages.lines.deleteLineError, 'error');
              }
              setLoading(false);
            }}
          />
          <DeleteManyDialog
            open={dialogs.bulkDelete}
            handleClose={() => dialogs.close('bulkDelete')}
            numLines={selectedLines === null ? 0 : selectedLines.length}
            deleteLineClick={async () => {
              dialogs.close('bulkDelete');
              setLoading(true);
              try {
                await Promise.all(
                  selectedLines.map((lineId) => deleteLine(lineId)),
                );
                snackbar.show(
                  messages.lines.success('Lines', 'deleted'),
                  'delete',
                );
                const filteredLines = lines.filter((line, i) => {
                  let deleted = false;
                  selectedLines.forEach((id) => {
                    if (id === line.id) {
                      deleted = true;
                    }
                  });
                  return !deleted && line;
                });
                setLines(filteredLines);
              } catch (err) {
                snackbar.show(messages.lines.bulkDeleteError, 'error');
              }
              setLoading(false);
            }}
          />
          <UploadDocumentDialog
            line={selectedLine && selectedLine.id}
            open={dialogs.upload}
            handleClose={(status) => {
              dialogs.close('upload');
              setSelectedLine(null);
              if (status === 'success') {
                snackbar.show(messages.lines.success('Document', 'uploadeds'));
              } else if (status === 'error') {
                snackbar.show(messages.lines.uploadDocumentError, 'error');
              }
            }}
          />
          <AddProductDialog
            open={dialogs.addProduct}
            handleClose={() => {
              dialogs.close('addProduct');
              setTimeout(() => {
                setSelectedProduct(null);
              }, 500);
            }}
            addProductToState={(newProduct: IProduct) => {
              setProducts(products ? [...products, newProduct] : [newProduct]);
            }}
            selectedProduct={selectedProduct}
            setProducts={setProducts}
            products={products}
          />
          <AutoCreateLineDialog lineToTemplate={selectedLine} />
          <EditActiveLineDialog
            open={dialogs.lineChange}
            handleClose={() => dialogs.close('lineChange')}
            currentStep={requestChangeCurrentStep}
          />
        </>
      )}
    </LinesContext.Provider>
  );
}

export const useLinesContext = () => useContext(LinesContext);
